import Accordion from "react-bootstrap/Accordion";
import {AccordionToggleProps} from "react-bootstrap/AccordionToggle"
import {AccordionCollapseProps} from "react-bootstrap/AccordionCollapse"
import {useToggle} from "@Hooks/dispatch/useToggle";

interface AccordionItemProps<T> {
    eventKey: string,
    accordionToggleProps?: Omit<AccordionToggleProps, "eventKey">,
    Header: React.ComponentType<T & { opened?: boolean }>,
    headerProps?: Omit<T, "opened">,
    accordionCollapseProps?: Omit<AccordionCollapseProps, "eventKey" | "children">,
    children: React.ReactNode,
}

/**
 * Accordion Item component used to remove repeatable accordion logic from the code and unify "opened"\"closed" setting logic
 */
function AccordionItem<T>(props: AccordionItemProps<T>) {
    const {
        eventKey,
        accordionToggleProps,
        headerProps,
        Header,
        accordionCollapseProps,
        children,
    } = props

    const {
        value: opened,
        set: setOpened,
    } = useToggle()

    const prepareAccordionCollapseProps = () => {
        const onEnter = (node: HTMLElement) => {
            setOpened(true)
            if (accordionCollapseProps?.onEnter) {
                accordionCollapseProps.onEnter(node)
            }
        }

        const onExit = (node: HTMLElement) => {
            setOpened(false)
            if (accordionCollapseProps?.onExit) {
                accordionCollapseProps?.onExit(node)
            }
        }

        return {
            ...accordionCollapseProps,
            onEnter,
            onExit,
        }
    }

    return (
        <>
            <Accordion.Toggle
                eventKey={eventKey}
                {...accordionToggleProps}
            >
                <Header
                    opened={opened}
                    {...headerProps}
                />
            </Accordion.Toggle>
            <Accordion.Collapse
                eventKey={eventKey}
                {...prepareAccordionCollapseProps()}
            >
                <>
                    {children}
                </>
            </Accordion.Collapse>
        </>
    )
}

export default AccordionItem